import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { showApiErrorToast } from '@/utils/errors';
import { feedbackQueryKeys, markFeedbackRead } from '../api/feedback.api';
import type { PaginatedFeedbacks } from '../types/feedback.types';

type FeedbackListPages = InfiniteData<PaginatedFeedbacks>;

export function useMarkFeedbackRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => markFeedbackRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: feedbackQueryKeys.lists() });

      const snapshots = queryClient.getQueriesData<FeedbackListPages>({
        queryKey: feedbackQueryKeys.lists(),
      });

      const now = new Date();
      queryClient.setQueriesData<FeedbackListPages>(
        { queryKey: feedbackQueryKeys.lists() },
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              data: page.data.map((item) =>
                item.id === id && item.readAt === null ? { ...item, readAt: now } : item,
              ),
            })),
          };
        },
      );

      return { snapshots };
    },
    onError: (error, _id, context) => {
      context?.snapshots.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      showApiErrorToast(error, 'Failed to mark as read');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackQueryKeys.all() });
    },
  });
}
